import { Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { LayoutService } from '../../service/layout.service';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../../../core/services/auth.service';
import { firstValueFrom, forkJoin, lastValueFrom, map, of, switchMap, take, tap } from 'rxjs';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { PanelModule } from 'primeng/panel';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfigurationsComponent } from '../../../configurations/configurations.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NotificationService } from '../../../../core/services/notification.service';
import { EmployeeService } from '../../../../core/services/employee.service';
import { NotificationDetails } from '../../../../core/models/notificationDetails';
import { NotificationReadService } from '../../../../core/services/notification-read.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { EmployeeMini } from '../../../../core/models/employee-mini';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TrackedDialogService } from '../../../../core/services/tracked-dialog.service';
import { EmPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { Permission } from '../../../../core/enums/permission.enum';
import { PermissionService } from '../../../../core/services/permission.service';
import { UserProfile } from '../../../../core/models/user.model';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { PasswordModule } from 'primeng/password';
import { DialogModule } from 'primeng/dialog';
import { PermissionBit } from '../../../../core/utils/permission-types';
import { MenuService } from '../sidebar/menu.service';
import { nodeLink } from '../sidebar/menu-tree';
import { profileInitials, sentenceCaseIfShouting } from './header-helpers';
import { CONFIGURATION_PERMISSIONS, headerCommands } from './header-commands';
import { CommandPaletteService } from '../command-palette/command-palette.service';
import { shortcutLabel } from '../command-palette/keyboard-shortcuts';

@Component({
  selector: 'app-topbar',
  standalone: true,
  templateUrl: './header.component.html',
  imports: [
    CommonModule,
    RouterModule,
    TooltipModule,
    OverlayPanelModule,
    ButtonModule,
    DividerModule,
    MenuModule,
    BadgeModule,
    PanelModule,
    ConfirmDialogModule,
    ProgressSpinnerModule,
    TableModule,
    TranslateModule,
    EmPermissionDirective,
    DialogModule,
    PasswordModule,
    ReactiveFormsModule
  ],
  providers: [TrackedDialogService],
})
export class HeaderComponent implements OnInit, OnDestroy {
  isRestrictedMode: boolean = false;

  items!: MenuItem[];

  @ViewChild('menubutton') menuButton!: ElementRef;

  @ViewChild('topbarmenubutton') topbarMenuButton!: ElementRef;

  @ViewChild('topbarmenu') menu!: ElementRef;

  @ViewChild('userProfilePanel') userProfilePanel!: any;

  @ViewChild('languageMenuPanel') languageMenuPanel!: OverlayPanel;

  profile: WritableSignal<UserProfile | undefined> = signal(undefined);

  isDarkMode = computed(() => this.layoutService.config().colorScheme === 'dark');

  /** Initials for the profile avatar ('' until the profile is loaded). */
  initials = computed(() => profileInitials(this.profile()?.fullName));

  /** Menu path of the current page (section > group > page). */
  readonly breadcrumb = inject(MenuService).activePath;

  /** A crumb opens its own page, or the first page below it. */
  readonly crumbLink = nodeLink;

  /** Section titles are written in capitals for the sidebar headings; the breadcrumb shows them like the other crumbs. */
  readonly crumbText = sentenceCaseIfShouting;

  readonly commandPalette = inject(CommandPaletteService);

  /** Shortcuts shown on the palette trigger and in the sidebar toggle tooltip. */
  readonly shortcutLabel = shortcutLabel('K');
  readonly sidebarShortcutLabel = shortcutLabel('B');

  readonly configurationPermissions = CONFIGURATION_PERMISSIONS;

  private unregisterCommands?: () => void;

  menuLanguage = signal<boolean>(false);

  languageItems: MenuItem[] | undefined;
  notification = signal<boolean>(false);
  translations: any = {};
  Permission = Permission;
  isNotificationPop: boolean = false;
  notificationEmpty: boolean = true;
  newNotification: number = 0;
  popItem!: NotificationDetails;
  isLoadingEmployees: boolean = false;
  isOwnPageOnly: boolean = false;

  notificationMenu: NotificationDetails[] = [];
  employees: EmployeeMini[] = [];
  ref: DynamicDialogRef | undefined;

  changePasswordDialogVisible = false;
  changePasswordForm!: FormGroup;
  isChangingPassword = false;

  private readonly TYPE_PERMISSIONS: Record<string, PermissionBit> = {
  'CDD': Permission.CONTRACT_ENDING_NOTIFICATION,
  'ANAPEC': Permission.CONTRACT_ENDING_NOTIFICATION,
  'TRIAL PERIOD': Permission.TRIAL_PERIOD_ENDING_NOTIFICATION,
  'JUNIOR PROMOTION': Permission.JUNIOR_PROMOTION_NOTIFICATION,
  };

  constructor(
    public layoutService: LayoutService,
    private authService: AuthService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private employeeService: EmployeeService,
    private notificationReadService: NotificationReadService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private router: Router
  ) {
    this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    this.layoutService.isRestrictedMode$.subscribe(isRestricted => {
        this.isRestrictedMode = isRestricted;
    });

    this.initChangePasswordForm();
  }
  

  ngOnInit() {
    this.checkIfOwnPageOnly();
    this.languageItems = [
      {
        label: 'English', // i18n-ignore
        code: 'en',
      },
      {
        label: 'Español', // i18n-ignore
        code: 'es',
      },
      {
        label: 'العربية', // i18n-ignore
        code: 'ar',
      },
    ];

    const savedLang = localStorage.getItem('preferredLang') ?? 'en';

    this.loadCurrentUserProfile();
    this.changeLanguage(savedLang);
    this.loadTranslations();

    this.unregisterCommands = this.commandPalette.register(() =>
      headerCommands({
        isDarkMode: this.isDarkMode(),
        currentLanguage: this.translate.currentLang,
        languages: (this.languageItems ?? []).map((item) => ({ code: item['code'], label: item.label ?? item['code'] })),
        canOpenConfigurations: !this.isRestrictedMode && this.permissionService.hasAnyPermission(CONFIGURATION_PERMISSIONS),
        setDarkMode: (dark) => this.changeTheme(dark),
        setLanguage: (code) => this.changeLanguage(code),
        openConfigurations: () => this.openConfigurations(),
        changePassword: () => this.showChangePasswordDialog(),
        logout: () => this.logout(),
      }),
    );
  }

  ngOnDestroy() {
    this.unregisterCommands?.();
  }

  private canSeeNotification(n: NotificationDetails): boolean {
  const required = this.TYPE_PERMISSIONS[n.notificationType?.type?.trim().toUpperCase() ?? ''];
  return required ? this.permissionService.hasPermission(required) : true;
  }
  private checkIfOwnPageOnly(): void {
    const userPermissionsBitmask = this.permissionService.getUserPermissions();
    const ownPageBit = Permission.CARD_VIEW_OWN_PAGE;

    this.isOwnPageOnly = userPermissionsBitmask === ownPageBit;

    if (this.isOwnPageOnly) {
      this.layoutService.hidSideBar();
    }


  }
  loadCurrentUserProfile() {
    if (this.authService.isLoggedIn()) {
      firstValueFrom(this.authService.loadUserInfo())
        .then((userProfile) => {
          this.profile.set(userProfile);
          this.loadNotificationMenu();
        })
        .catch(() => {});
    }
  }

  loadTranslations() {
    this.translate.get(['layout.configurations.title']).subscribe((translations) => {
      this.translations = translations;
      console.log(translations);
    });
  }

  logout() {
    this.authService.logout();
  }

  changeTheme(isDarkMode: boolean) {
    this.notification.set(false);
    this.menuLanguage.set(false);
    this.layoutService.toggleDarkMode(isDarkMode);
  }

  openNotificationMenu(event: Event, panel: OverlayPanel): void {
    this.loadNotificationMenu();
    panel.toggle(event);
  }

  private loadNotificationMenu() {
    const userEmail = this.profile()?.email || '';
    if (userEmail) {
      this.notificationService
        .getLastNotifications(userEmail)
        .pipe(
          map((notifications) => notifications.filter((n) => this.canSeeNotification(n))),
          switchMap((notifications) => {
            if (notifications.length === 0) {
              return [];
            }

            const readStatusRequests = notifications.map((notification) =>
              this.notificationReadService.isNotificationReadable(notification.id!, userEmail).pipe(
                map((response: any) => {
                  const isReadable = response.isReadable;
                  return { ...notification, isReadable };
                }),
              ),
            );

            return forkJoin(readStatusRequests);
          }),
        )
        .subscribe({
          next: (notifications) => {
            this.notificationMenu = notifications;
            this.newNotification = notifications.filter((notification) => !notification.isReadable).length;
            this.notificationEmpty = notifications.length === 0;
          },
          error: (err) => {
            console.error('Error fetching notifications:', err);
            this.newNotification = 0;
            this.notificationEmpty = true;
          },
        });
    }
  }

  loadAllNotifications() {
    const userEmail = this.profile()?.email || '';

    if (userEmail) {
      this.notificationService
        .getAllNotifications(userEmail)
        .pipe(
          map((notifications) => notifications.filter((n) => this.canSeeNotification(n))),
          switchMap((notifications) => {
            if (notifications.length === 0) {
              return [];
            }

            const readStatusRequests = notifications.map((notification) =>
              this.notificationReadService.isNotificationReadable(notification.id!, userEmail).pipe(
                map((response: any) => {
                  const isReadable = response.isReadable;
                  return { ...notification, isReadable };
                }),
              ),
            );

            return forkJoin(readStatusRequests);
          }),
        )
        .subscribe({
          next: (notifications) => {
            this.notificationMenu = notifications;
            this.newNotification = notifications.filter((notification) => !notification.isReadable).length;
            this.notificationEmpty = notifications.length === 0;
          },
          error: (err) => {
            console.error('Error fetching notifications:', err);
            this.newNotification = 0;
            this.notificationEmpty = true;
          },
        });
    }
  }

  popNotification(item: NotificationDetails) {
    this.isNotificationPop = true;
    this.notification.set(false);
    this.popItem = item;
    this.isLoadingEmployees = true;
    this.employees = item.employees || [];
    this.isLoadingEmployees = false;
    this.markNotificationAsRead(item);
    this.navigateForValidationNotification(item);
  }

  private navigateForValidationNotification(item: NotificationDetails): void {
    const type = item.notificationType?.type;
    if (type === 'ROLE VALIDATION') {
      this.router.navigate(['/employees/role-validation']);
    }
    if (type === 'SALARY VALIDATION') {
      this.router.navigate(['/employees/salary-validation']);
    }
    if (type === 'CDD' || type === 'ANAPEC') {
      this.router.navigate(['/employees/contract-ending']);
    }
    if (type === 'TRIAL PERIOD') {
      this.router.navigate(['/employees/trial-period']);
    }
    if (type === 'JUNIOR PROMOTION') {
      this.router.navigate(['/employees/junior-promotion']);
    }
  }

  markNotificationAsRead(item: NotificationDetails) {
    this.notificationReadService.markAsRead(item.id || '', this.profile()?.email || '').subscribe({
      next: () => {
        this.loadNotificationMenu();
      },
      error: (err) => {
        console.error('Error marking notification as read:', err);
      },
    });
  }

  markAllNotificationsAsRead() {
    const userEmail = this.profile()?.email || '';
    if (!userEmail) {
      console.warn('No user email found.');
      return;
    }

    this.notificationReadService.markAllAsRead(userEmail).subscribe({
      next: () => {
        this.loadNotificationMenu();
      },
      error: (err) => {
        console.error('Error marking all notifications as read:', err);
      },
    });
  }

  togglePopup() {
    this.isNotificationPop = !this.isNotificationPop;
  }

  closePopup(event: MouseEvent) {
    this.isNotificationPop = false;
  }

  toggleNotification() {
    this.notification.set(!this.notification());
    this.menuLanguage.set(false);
  }

  toggleLanguageMenu() {
    this.notification.set(false);
    this.menuLanguage.set(!this.menuLanguage());
  }

  toggleUserProfilePanel(event: Event) {
    this.notification.set(false);
    this.menuLanguage.set(false);

    this.userProfilePanel.toggle(event);
  }

  openConfigurations() {
    this.ref = this.dialogService.open(ConfigurationsComponent, {
      header: this.translations['layout.configurations.title'],
      width: '80vw',
      height: '90vh',
      contentStyle: { padding: '0' },
    });
  }

  changeLanguage(lang: string) {
    this.notification.set(false);
    this.menuLanguage.set(false);
    if (this.languageMenuPanel) {
      this.languageMenuPanel.hide();
    }

    this.translate.use(lang);
    localStorage.setItem('preferredLang', lang);
    this.setDirection(lang);
  }

  setDirection(lang: string) {
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
  }

  initChangePasswordForm() {
    this.changePasswordForm = new FormGroup({
      currentPassword: new FormControl('', {
                      nonNullable: true,
                      validators: [Validators.required],
                  }),
      newPassword: new FormControl('', {
                nonNullable: true,
                validators: [Validators.required, Validators.minLength(8), this.strongPasswordValidator],
            }),
      confirmPassword: new FormControl('', {
                      nonNullable: true,
                      validators: [Validators.required],
                  })
    }, { validators: [this.passwordMatchValidator, this.newPasswordDifferentValidator] });
  }

  strongPasswordValidator(control: AbstractControl): ValidationErrors | null {
      const value = control.value || '';

      const strong =
        /[A-Z]/.test(value) &&
        /[a-z]/.test(value) &&
        /[0-9]/.test(value) &&
        /[^A-Za-z0-9]/.test(value);

      return strong ? null : { weakPassword: true };
  }

  newPasswordDifferentValidator(control: AbstractControl): ValidationErrors | null {
    const currentPassword = control.get('currentPassword')?.value;
    const newPassword = control.get('newPassword')?.value;

    if (!currentPassword || !newPassword) {
      return null;
    }

    return currentPassword !== newPassword ? null : { sameAsCurrentPassword: true };
  }

  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;

    return password === confirmPassword ? null : { mismatch: true };
  }

  showChangePasswordDialog() {
    this.userProfilePanel.hide();
    this.changePasswordForm.reset();
    this.changePasswordDialogVisible = true;
  }

  onChangePasswordSubmit() {
    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    this.isChangingPassword = true;
    const { currentPassword, newPassword } = this.changePasswordForm.value;

    this.authService.changePassword(currentPassword, newPassword).subscribe({
      next: () => {
        this.messageService.add({ key: 'globalToast', severity: 'success', summary: this.translate.instant('common.toast.success'), detail: this.translate.instant('layout.header.passwordChangedSuccess') });
        this.changePasswordDialogVisible = false;
        this.isChangingPassword = false;
        this.authService.logout();
      },
      error: (err) => {
        this.isChangingPassword = false;
        if (err.status === 400) {
          this.changePasswordForm.get('currentPassword')?.setErrors({ incorrect: true });
          console.log(err);
        } else {
          this.messageService.add({ key: 'globalToast', severity: 'error', summary: this.translate.instant('common.toast.error'), detail: this.translate.instant('layout.header.passwordChangeError') });
        }
      }
    });
  }
}
